/** @jsxImportSource @emotion/react */
import { useCallback, useContext } from 'react';
import isMobile from 'ismobilejs';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';
import { MessageRegenerateResponseFooter } from '@trinity/chat-components/components/presentation/Message.RegenerateResponse.Footer';
import { CapabilitiesContext } from '../BrandInsights/context';
import { RegenerateMessageContext } from './context';
import { SystemAuthor } from './Message.Author';
import { ErrorTrace } from './Message.ErrorTrace';
import { useStyles } from './Message.System.styles';
import { ChatMessageVariantSelector } from './Message.VariantSelector';

type Props = {
  className?: string;
  item: Chat.Query;
  traceId?: string | null;
  hasRegenerateFooter: boolean;
};

export const SystemUserErrorMessage = ({ className, item, traceId, hasRegenerateFooter }: Props) => {
  const device = isMobile();
  const styles = useStyles();
  const { showAuthor } = useAvatarPanel();
  const capabilities = useContext(CapabilitiesContext);

  const showLangfuseTrace = capabilities.canViewTraces && !!item.langfuseTraceUrl;

  return (
    <div css={styles.root} className={className}>
      {showAuthor && !device.phone && (
        <div css={styles.authorWrap}>
          <SystemAuthor css={styles.author} />
          <ChatMessageVariantSelector item={item} />
        </div>
      )}
      <div>
        <div css={styles.body}>
          There was an error processing your request.
          {traceId && <ErrorTrace traceId={traceId} />}
          {showLangfuseTrace && item.langfuseTraceUrl && (
            <Link href={item.langfuseTraceUrl} target="_blank">
              View Langfuse trace
            </Link>
          )}
        </div>
        {hasRegenerateFooter && <RegenerateResponse item={item} />}
      </div>
    </div>
  );
};

type RegenerateResponseProps = {
  item: Chat.Query;
};

const RegenerateResponse = ({ item }: RegenerateResponseProps) => {
  const { onRegenerate } = useContext(RegenerateMessageContext);

  const handleClick = useCallback(() => {
    onRegenerate(item);
  }, [item, onRegenerate]);

  return <MessageRegenerateResponseFooter css={css({ marginTop: 10 })} onClick={handleClick} />;
};

const Link = styled.a(({ theme }) => ({
  color: theme.palette.hyperlink,
  marginTop: 10,
  display: 'block',
}));
